import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Select } from '@customerio/pluma-components/react';
import { useDebouncedValue } from '@/hooks/state/use-debounced-value';
import { useDeliveryFiltersOptional } from '@/services/db/delivery/queries';
import { orderedMessagesFor, withDeletedPlaceholder, type GroupingLabels } from './message-grouping';
import { segmentablePickerData, type PickerMessage } from './message-picker-data';
import {
  computeHasMoreResults,
  ensureSelectedOptionPresent,
  mergeGroupedOptions,
  pageContainingMessage,
  queryThroughPage,
  virtualQuery,
  type MessageSelectOption,
} from './message-select-pagination';

const LEGACY_SMS_TYPE = 'twilio';
const SMS_MESSAGE_CHANNEL_TYPE = 'sms';

type MessageSelectProps = {
  channel: string;
  /**
   * The selected message value in the picker grammar ('*', 'push_*',
   * 'output_11', 'newsletter_3', 'any_email-campaign_4', ...). An empty
   * string means no message has been picked yet.
   */
  value: string;
  /** Whether the plan allows anonymous in-app broadcast entries. */
  allowInAppBroadcasts: boolean;
  disabled?: boolean;
  /** A row-owned validation message; takes precedence over the picker's own deleted-message error. */
  error?: string;
  onChange: (eventName: string) => void;
  onOpenChange?: (isOpen: boolean) => void;
};

function isAnyValue(value: string): boolean {
  return value === '*' || value.endsWith('_*') || value.startsWith('any_');
}

const GROUPING_LABELS: GroupingLabels = {
  campaignNoun: 'automation',
  campaignsPlural: 'Automations',
  newslettersPlural: 'One-time sends',
};

function useOrderedMessages(channel: string, value: string, allowInAppBroadcasts: boolean, labels: GroupingLabels) {
  const filters = useDeliveryFiltersOptional();
  const pickerData = useMemo(() => (filters.data == null ? null : segmentablePickerData(filters.data)), [filters.data]);

  // Legacy Twilio conditions can also target messages from the newer SMS channel.
  const segmentableTypes = useMemo(
    () => (channel === LEGACY_SMS_TYPE ? [LEGACY_SMS_TYPE, SMS_MESSAGE_CHANNEL_TYPE] : [channel]),
    [channel],
  );

  const isDeleted = useMemo(() => {
    if (pickerData == null || value === '' || isAnyValue(value)) {
      return false;
    }
    return !pickerData.segmentable.some(
      (message) => segmentableTypes.includes(message.type) && message.output_id === value,
    );
  }, [pickerData, value, segmentableTypes]);

  const orderedMessages: PickerMessage[] = useMemo(() => {
    if (pickerData == null) {
      return [];
    }
    const ordered = orderedMessagesFor(
      pickerData.segmentable,
      {
        types: segmentableTypes,
        type: channel,
        // Per-variation entries render only where a surface opts in — the
        // people-search picker excludes them.
        includeVariations: false,
        includeLanguageBranches: true,
        allowWildcardSelection: true,
        allowInParentWildcardSelection: true,
        allowInAppBroadcastSelection: allowInAppBroadcasts,
      },
      labels,
    );
    return withDeletedPlaceholder(ordered, value, isDeleted, channel);
  }, [pickerData, segmentableTypes, channel, allowInAppBroadcasts, labels, value, isDeleted]);

  return { orderedMessages, isDeleted, isLoadingCatalog: filters.isLoading };
}

/**
 * How long a load-more stays pending before its page lands. The menu suppresses
 * further load-more calls for as long as it is pending, so a saved selection
 * that sits past the first page pages in one step at a time, with a spinner,
 * instead of every intervening page arriving in a single frame.
 */
const LOAD_MORE_DELAY_MS = 200;

/**
 * The pages the picker starts from: enough to include the current selection at
 * its real position in the grouped list, so no selection has to be appended
 * past the end of the list. `list` is the array the pages were built from,
 * compared by identity to tell one filtered list from another.
 */
type BaselinePages = { list: PickerMessage[]; searchText: string; page: number; options: MessageSelectOption[] };

/**
 * The pages load-more has added on top of a baseline. The baseline fields
 * record which baseline they extend, so pages merged from a list that has
 * since changed are discarded rather than shown against the new one.
 */
type LoadedPages = {
  baselineList: PickerMessage[];
  baselineSearchText: string;
  baselinePage: number;
  pagesAdded: number;
  options: MessageSelectOption[];
};

/**
 * Whether `loaded`'s pages were built from the same filtered list and starting
 * page as `baseline`. The catalog array keeps its identity for as long as the
 * catalog, channel and labels do, and the search text narrows that array into
 * the list actually paged over, so both identify it together; the page pins
 * which slice of it the pages extend.
 */
function extendsBaseline(loaded: LoadedPages, baseline: BaselinePages): boolean {
  return (
    loaded.baselineList === baseline.list &&
    loaded.baselineSearchText === baseline.searchText &&
    loaded.baselinePage === baseline.page
  );
}

/**
 * The picker's accumulated pages, starting from `baseline` — the pages needed
 * to show the current selection in place. Each load-more merges one further
 * page onto what is already displayed, so the grouped list grows one page at a
 * time and the options already on screen keep their object identity.
 *
 * The page count lives here as pages *added* to the baseline, and the page on
 * screen is derived from the current baseline, so a baseline that resolves
 * after mount — the catalog arrives, or the selection moves — needs no
 * reconciliation.
 *
 * `queryPage` and `baseline` go through a ref so the timer callback always
 * reads the latest catalog, search text and labels without re-arming.
 */
function useMessagePaging(queryPage: (page: number) => MessageSelectOption[], baseline: BaselinePages) {
  const [loaded, setLoaded] = useState<LoadedPages | null>(null);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const timeoutRef = useRef<number | null>(null);
  const sourceRef = useRef({ queryPage, baseline });
  sourceRef.current = { queryPage, baseline };

  const cancel = useCallback(() => {
    if (timeoutRef.current != null) {
      window.clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  useEffect(() => cancel, [cancel]);

  const resetPaging = useCallback(() => {
    cancel();
    setLoaded(null);
    setIsLoadingMore(false);
  }, [cancel]);

  const loadMore = useCallback(() => {
    if (timeoutRef.current != null) {
      return;
    }
    setIsLoadingMore(true);
    timeoutRef.current = window.setTimeout(() => {
      timeoutRef.current = null;
      setLoaded((current) => {
        const { queryPage: query, baseline: base } = sourceRef.current;
        const isCurrent = current != null && extendsBaseline(current, base);
        const pagesAdded = (isCurrent ? current.pagesAdded : 0) + 1;
        const existing = isCurrent ? current.options : base.options;
        return {
          baselineList: base.list,
          baselineSearchText: base.searchText,
          baselinePage: base.page,
          pagesAdded,
          options: mergeGroupedOptions(existing, query(base.page + pagesAdded)),
        };
      });
      setIsLoadingMore(false);
    }, LOAD_MORE_DELAY_MS);
  }, []);

  // Pages added against a superseded baseline are dropped: the baseline itself
  // already covers the current filtered list.
  const extended = loaded != null && extendsBaseline(loaded, baseline) ? loaded : null;

  return {
    page: extended == null ? baseline.page : baseline.page + extended.pagesAdded,
    options: extended?.options ?? baseline.options,
    isLoadingMore,
    loadMore,
    resetPaging,
  };
}

/**
 * The options the menu renders, plus its load-more controls. Paging starts at
 * the page holding the current selection, so the selected entry renders where
 * it belongs in the grouped list. Appending it past the end instead would put
 * it at the last position, and the menu scrolls the selected option into view
 * on every page that lands until the selection's own page arrives.
 */
function useMessageOptions(
  orderedMessages: PickerMessage[],
  value: string,
  searchText: string,
  labels: GroupingLabels,
) {
  const selectedMessage = useMemo(
    () => orderedMessages.find((message) => message.output_id === value) ?? null,
    [orderedMessages, value],
  );

  const queryPage = useCallback(
    (pageNumber: number) => virtualQuery(orderedMessages, searchText, pageNumber, labels),
    [orderedMessages, searchText, labels],
  );

  const baseline: BaselinePages = useMemo(() => {
    const selectedId = selectedMessage?.output_id;
    const page = selectedId == null ? 1 : pageContainingMessage(orderedMessages, searchText, selectedId);
    return {
      list: orderedMessages,
      searchText,
      page,
      options: queryThroughPage(orderedMessages, searchText, page, labels),
    };
  }, [orderedMessages, searchText, labels, selectedMessage]);

  const { page, options: pagedOptions, isLoadingMore, loadMore, resetPaging } = useMessagePaging(queryPage, baseline);

  const options: MessageSelectOption[] = useMemo(
    () => ensureSelectedOptionPresent(pagedOptions, selectedMessage),
    [pagedOptions, selectedMessage],
  );

  return {
    options,
    selectedMessage,
    hasMoreResults: computeHasMoreResults(orderedMessages, searchText, page),
    isLoadingMore,
    loadMore,
    resetPaging,
  };
}

/**
 * The message picker: "any {channel}" wildcards, per-campaign wildcards, and
 * specific campaign/broadcast/newsletter/transactional messages, grouped by
 * their parent. The workspace's whole segmentable list is in memory, so
 * search and load-more page it client-side.
 */
export function MessageSelect({
  channel,
  value,
  allowInAppBroadcasts,
  disabled,
  error,
  onChange,
  onOpenChange,
}: MessageSelectProps) {
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch] = useDebouncedValue(searchInput, 300);

  const labels = GROUPING_LABELS;
  const { orderedMessages, isDeleted, isLoadingCatalog } = useOrderedMessages(
    channel,
    value,
    allowInAppBroadcasts,
    labels,
  );

  const { options, selectedMessage, hasMoreResults, isLoadingMore, loadMore, resetPaging } = useMessageOptions(
    orderedMessages,
    value,
    debouncedSearch,
    labels,
  );

  const errorMessage = error ?? (isDeleted ? 'The selected message has been deleted' : undefined);

  const handleSelect = (selected: string | null) => {
    if (selected == null) {
      return;
    }
    const item = orderedMessages.find((message) => message.output_id === selected);
    if (item == null) {
      return;
    }
    // Language-branch entries ("All Variations") carry a synthetic output_id;
    // the selectable value is their action id.
    onChange(item.isLanguageBranch === true && item.id != null ? `output_${item.id}` : selected);
  };

  return (
    <Select
      data-testid="dryad-message-filter-select"
      ariaLabel="Message"
      size="sm"
      placeholder="Select a message"
      // The default renderer lets this nested grouped menu grow to the whole
      // viewport width; bound it so the dropdown anchors under the trigger
      // like the picker it replaces (verified live at this width).
      unsafe_popoverSize={{ maxWidth: 480 }}
      options={options}
      value={selectedMessage?.output_id}
      isSearchable
      searchPlaceholder="Search…"
      searchInputValue={searchInput}
      onSearchInput={(event) => {
        setSearchInput(event.target.value);
        resetPaging();
      }}
      onLoadMore={loadMore}
      isLoadingMore={isLoadingMore}
      shouldLoadMore={hasMoreResults}
      isLoading={isLoadingCatalog || searchInput !== debouncedSearch}
      onOpenChange={(isOpen: boolean) => {
        if (!isOpen) {
          setSearchInput('');
          resetPaging();
        }
        onOpenChange?.(isOpen);
      }}
      onChange={handleSelect}
      error={errorMessage}
      isDisabled={disabled === true || isLoadingCatalog}
    />
  );
}
